import React, { useState } from 'react';
import { Input, Modal } from '@mui/material';
import { HighlightOff } from '@mui/icons-material';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Title from '../Title/Title';
import {
  DescriptionText,
  ModalHeader,
  CloseButton,
  UploadButtonContainer,
  StyledModalBox,
  UploadContainer,
  StyledButton,
  StyledContent,
  FileUploadIcon,
  FileInputLabel,
  ErrorText,
  CSVFileSizeLimitText,
  StyledLoaderWrapper,
  UploadCSVContainer,
  UploadFileBox,
  FileNameWrapper,
  FileNameText,
  FileDeleteIcon,
  StyledUploadFileIcon,
  DomainsErrorContainer,
  ErrorContent,
  StyledBox,
  StyledNormal,
  StyledStrong,
  StyledErrorIcon
} from './Style/UploadCSVModal.style';
import { UploadCSVModalProps } from '../../types/UploadCSVModalTypes';
import Loader from '../Loader/Loader';

const UploadCSVModal: React.FC<UploadCSVModalProps> = ({
  open,
  handleClose,
  fileType = '.csv',
  maxSize = 5242880, // 5MB
  onUpload,
  modalTitle,
  description,
  buttonText,
  error,
  renderErrorData,
  loading,
}) => {
  const [fileError, setFileError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);


  const setFileToUpload = (file: File) => {
    if (file && file?.size < maxSize) {
      setFileError(null);
      setFile(file);
    } else {
      setFileError(
        `File exceeds the maximum size of ${maxSize / (1024 * 1024)} MB`,
      );
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files ? event.target?.files?.[0] : null;
    if(file){
      setFileToUpload(file)
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFile = event.dataTransfer?.files?.[0];
    if(droppedFile){
      setFileToUpload(droppedFile)
    }
  };

  const handleFileInputClick = (
    event: React.SyntheticEvent<HTMLLabelElement>,
  ) => {
    if (
      event instanceof KeyboardEvent &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      document.getElementById('file-input')?.click();
    } else if (event instanceof MouseEvent) {
      document.getElementById('file-input')?.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUpload = () => {
    if (file) {
      onUpload(file);
    }
  };

  const handleDeleteFile = () => {
    setFile(null);
    setFileError(null);
  };
  const renderError = () => (
    <DomainsErrorContainer>
      <ErrorContent>
        <StyledBox>
          <StyledStrong>
            <StyledErrorIcon>
              <WarningAmber sx={{ width: 33, height: 33, color: 'warning.main' }} />
            </StyledErrorIcon>
            {error?.title}
          </StyledStrong>
          <ErrorContent sx={StyledContent}>
            <StyledStrong sx={{ fontSize: '12px' }}>
              {error?.description}
            </StyledStrong>
          </ErrorContent>
        </StyledBox>
      </ErrorContent>
      {renderErrorData || <></>}
      <UploadButtonContainer>
        <StyledButton
          variant="contained"
          disabled={loading}
          onClick={handleClose}>
          Continue
        </StyledButton>
      </UploadButtonContainer>
    </DomainsErrorContainer>
  );

  const renderUploadSection = () => (
    <>
      {loading ? (
        <StyledLoaderWrapper>
          <StyledNormal>We are processing the CSV...</StyledNormal>
          <Loader size={25} />
        </StyledLoaderWrapper>
      ) : (
        <UploadCSVContainer onDragOver={handleDragOver} onDrop={handleDrop}>
          <DescriptionText>{description}</DescriptionText>
          <UploadContainer>
            {file ? (
              <UploadCSVContainer
                sx={{ justifyContent: 'center', marginTop: 0 }}>
                <FileNameWrapper>
                  <StyledUploadFileIcon />
                  <FileNameText>{file.name}</FileNameText>
                  <FileDeleteIcon onClick={handleDeleteFile} data-testid="delete-file-icon">
                    <HighlightOff style={{ fontSize: 18 }} />
                  </FileDeleteIcon>
                </FileNameWrapper>
              </UploadCSVContainer>
            ) : (
              <>
                <FileUploadIcon />
                <UploadFileBox>
                  <StyledStrong size="small">Drag and drop or </StyledStrong>
                  <FileInputLabel
                    htmlFor="file-input"
                    onClick={handleFileInputClick}
                    tabIndex={0}>
                    Choose file
                  </FileInputLabel>{' '}
                  <StyledStrong size="small">to upload</StyledStrong>
                </UploadFileBox>
                <CSVFileSizeLimitText>
                  CSV file max. {maxSize / (1024 * 1024)} MB
                </CSVFileSizeLimitText>
                {fileError && <ErrorText>{fileError}</ErrorText>}
                <Input
                  type="file"
                  id="file-input"
                  data-testid="file-input"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  inputProps={{
                    style: { cursor: 'pointer' },
                    accept: fileType,
                  }}
                />
              </>
            )}
          </UploadContainer>
          <UploadButtonContainer>
            <StyledButton
              variant="contained"
              disabled={loading || !file}
              onClick={handleUpload}>
              {buttonText}
            </StyledButton>
          </UploadButtonContainer>
        </UploadCSVContainer>
      )}
    </>
  );

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <StyledModalBox>
        <ModalHeader>
          <Title size={'small'} title={modalTitle} />
          <CloseButton onClick={handleClose} aria-label="close-modal-button">
            <HighlightOff />
          </CloseButton>
        </ModalHeader>
        {error ? renderError() : renderUploadSection()}
      </StyledModalBox>
    </Modal>
  );
};

export default UploadCSVModal;
